// src/lib/errors.ts
const map: Record<string, string> = {
  'auth/email-already-in-use': 'That email is already registered.',
  'auth/invalid-email': 'Please enter a valid email.',
  'auth/invalid-credential': 'Incorrect email or password. Please try again.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/user-disabled': 'This account has been disabled. Contact support for help.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error. Check your connection.',
  'auth/weak-password': 'Password is too weak. Use at least 8 characters with a mix of letters and numbers.',
  'auth/requires-recent-login': 'For your security, sign out and sign in again, then retry this action.',
  'auth/unauthorized-continue-uri': 'This site URL is not authorized for email actions. Contact support.',
  'auth/invalid-continue-uri': 'Could not complete email update. Please try again from Arevina.',
  'auth/missing-continue-uri': 'Could not complete email update. Please try again.',
  'permission-denied': "You don't have permission for that.",
  'auth/configuration-not-found': 'Firebase configuration error. Please check your settings.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled.',
  'auth/invalid-api-key': 'Invalid API key. Please check your configuration.',
  'auth/account-exists-with-different-credential':
    'An account with this email already exists. Please sign in with your email and password.',
  'auth/popup-blocked':
    'Your browser blocked the sign-in popup. Please allow popups for this site and try again.',
  'auth/unauthorized-domain':
    'Sign-in is not configured for this domain. Please contact support.',
  unavailable:
    'Could not connect to the server. Check your internet connection and try again.',
  'deadline-exceeded': 'The server took too long to respond. Please try again.',
};

export function friendly(e: any, fallback = 'Something went wrong.') {
  const code = e?.code || '';
  return map[code] || fallback;
}

/**
 * User-facing error copy for toasts/alerts — never returns raw Firebase/SDK messages.
 * Prefer this over `err.message` in UI. Reuses the same code map as `friendly()`.
 */
export function toUserFacingError(
  err: unknown,
  fallback = 'Something went wrong. Please try again.'
): string {
  const e = err as { code?: string; message?: string } | null | undefined;
  const code = e?.code || '';
  const raw = e?.message || '';
  const codeFromMessage = raw.match(/(?:auth|firestore|storage|functions)\/[\w-]+/)?.[0] || '';
  const resolved = code || codeFromMessage;
  if (resolved && map[resolved]) return map[resolved];
  // Firestore often surfaces codes without a prefix (e.g. permission-denied)
  if (code && map[code.replace(/^firestore\//, '')]) {
    return map[code.replace(/^firestore\//, '')];
  }
  if (raw.toLowerCase().includes('client is offline')) {
    return map.unavailable;
  }
  return fallback;
}

/** Human-readable sign-in errors — never expose raw Firebase SDK messages. */
export function authSignInError(err: unknown, fallback = 'Could not sign in. Please try again.'): string {
  return authAccountError(err, fallback);
}

/** Human-readable account-management errors (email, password, delete). */
export function authAccountError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const code = (err as { code?: string })?.code || '';
  const raw = (err as { message?: string })?.message || '';
  const codeFromMessage = raw.match(/(?:auth)\/[\w-]+/)?.[0] || '';
  const resolvedCode =
    code === 'auth/invalid-credential' ? 'auth/wrong-password' : code || codeFromMessage;
  if (resolvedCode && map[resolvedCode]) return map[resolvedCode];
  if (raw.toLowerCase().includes('client is offline')) {
    return map.unavailable;
  }
  if (raw.includes('auth/')) return fallback;
  if (raw && raw.length < 120 && !raw.includes('Firebase:')) return raw;
  return friendly(err, fallback);
}

